import React from "react";
import { Button } from "../Button";
import { DeleteConfirmationDialogProps } from "./DeleteConfirmationDialog.types";

const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  aircraftCallsign,
  theme = "dark",
  labels = {},
}) => {
  if (!isOpen) return null;

  const isDark = theme === "dark";

  // Default labels
  const defaultLabels = {
    deleteTitle: "Delete",
    confirmQuestion: "Are you sure you want to delete aircraft",
    warningText:
      "This action cannot be undone. The aircraft will be permanently removed from the system.",
    cancel: "Cancel",
    delete: "Delete",
  };

  const finalLabels = { ...defaultLabels, ...labels };

  // Overlay styles
  const overlayStyles: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    backdropFilter: "blur(8px)",
  };

  // Dialog container styles
  const dialogStyles: React.CSSProperties = {
    width: "508px",
    height: "280px",
    borderRadius: "4px",
    backgroundColor: isDark ? "#171215" : "#FFFFFF",
    boxShadow: isDark
      ? "0 20px 25px -5px rgba(0, 0, 0, 0.4), 0 10px 10px -5px rgba(0, 0, 0, 0.2)"
      : "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  };

  // Header styles
  const headerStyles: React.CSSProperties = {
    width: "508px",
    height: "64px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom: `1px solid ${isDark ? "#374151" : "#E5E7EB"}`,
    paddingTop: "16px",
    paddingRight: "20px",
    paddingBottom: "16px",
    paddingLeft: "20px",
  };

  // Content styles
  const contentStyles: React.CSSProperties = {
    width: "508px",
    height: "144px",
    minWidth: "192px",
    minHeight: "80px",
    display: "flex",
    flexDirection: "column",
    gap: "20px",
    padding: "20px",
    flex: 1,
  };

  // Footer styles
  const footerStyles: React.CSSProperties = {
    width: "508px",
    height: "72px",
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: "10px",
    borderTop: `1px solid ${isDark ? "#374151" : "#E5E7EB"}`,
    paddingTop: "16px",
    paddingRight: "20px",
    paddingBottom: "16px",
    paddingLeft: "20px",
  };

  // Text styles
  const titleStyles: React.CSSProperties = {
    fontSize: "18px",
    fontWeight: 600,
    color: isDark ? "#F9FAFB" : "#111827",
    margin: 0,
    fontFamily: "Inter, sans-serif",
  };

  const questionStyles: React.CSSProperties = {
    fontSize: "16px",
    fontWeight: 500,
    color: isDark ? "#F9FAFB" : "#111827",
    margin: 0,
    fontFamily: "Inter, sans-serif",
  };

  const descriptionStyles: React.CSSProperties = {
    fontSize: "14px",
    fontWeight: 400,
    color: isDark ? "#D1D5DB" : "#6B7280",
    margin: 0,
    fontFamily: "Inter, sans-serif",
    lineHeight: "1.5",
  };

  // Close button styles
  const closeButtonStyles: React.CSSProperties = {
    background: "none",
    border: "none",
    fontSize: "24px",
    color: isDark ? "#9CA3AF" : "#6B7280",
    cursor: "pointer",
    padding: "0",
    width: "24px",
    height: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  return (
    <div style={overlayStyles} onClick={onClose}>
      <div style={dialogStyles} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyles}>
          <h2 style={titleStyles}>
            {finalLabels.deleteTitle} {aircraftCallsign}
          </h2>
          <button style={closeButtonStyles} onClick={onClose}>
            ×
          </button>
        </div>

        {/* Content */}
        <div style={contentStyles}>
          <p style={questionStyles}>
            {finalLabels.confirmQuestion} {aircraftCallsign}?
          </p>
          <p style={descriptionStyles}>{finalLabels.warningText}</p>
        </div>

        {/* Footer */}
        <div style={footerStyles}>
          <Button
            variant="outline"
            onClick={onClose}
            theme={theme}
            style={{
              backgroundColor: "transparent",
              border: "1px solid #424242",
            }}
          >
            {finalLabels.cancel}
          </Button>
          <Button variant="danger" onClick={onConfirm} theme={theme}>
            {finalLabels.delete}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DeleteConfirmationDialog;
